import {
  buildReceiptLines,
  type ReceiptLine,
  type ReceiptOrder,
} from '@/lib/local-print-receipt-layout'

export { receiptDocumentLabel } from '@/lib/local-print-receipt-layout'

/** A sales order a receipt prints, with the shop logo the receipt shows. */
export type ReceiptRasterOrder = ReceiptOrder & {
  organization?: (NonNullable<ReceiptOrder['organization']> & { logo_url?: string | null }) | null
}

/** A one-bit receipt image, packed eight pixels per byte. */
export interface ReceiptRaster {
  widthPx: number
  heightPx: number
  dataBase64: string
}

const receiptPrinterDpi = 203
const testReceiptContentHeightPx = 56
const testReceiptFeedInches = 1.5
const testReceiptWidthPx = 128

function receiptCanvas() {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) throw new Error('This browser cannot draw receipts.')
  return { canvas, context }
}

function loadReceiptLogo(url: string | null | undefined): Promise<HTMLImageElement | null> {
  if (!url || typeof Image === 'undefined') return Promise.resolve(null)

  return new Promise((resolve) => {
    let settled = false
    const image = new Image()
    const finish = (value: HTMLImageElement | null) => {
      if (settled) return
      settled = true
      globalThis.clearTimeout(timeout)
      resolve(value)
    }
    const timeout = globalThis.setTimeout(() => finish(null), 3_000)

    image.crossOrigin = 'anonymous'
    image.onload = () => finish(image)
    image.onerror = () => finish(null)
    image.src = url
  })
}

function receiptLogoDimensions(
  image: HTMLImageElement | null,
  receiptWidth: number,
  padding: number
) {
  const sourceWidth = Number(image?.naturalWidth || image?.width || 0)
  const sourceHeight = Number(image?.naturalHeight || image?.height || 0)
  if (!sourceWidth || !sourceHeight) return null

  const maxWidth = receiptWidth - padding * 4
  const maxHeight = 96
  const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight)
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  }
}

function wrapReceiptText(context: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const words = text.trim().split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let current = ''

  const pushLongWord = (word: string) => {
    let chunk = ''
    for (const character of word) {
      const candidate = `${chunk}${character}`
      if (chunk && context.measureText(candidate).width > maxWidth) {
        lines.push(chunk)
        chunk = character
      } else {
        chunk = candidate
      }
    }
    return chunk
  }

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    if (context.measureText(candidate).width <= maxWidth) {
      current = candidate
      continue
    }
    if (current) {
      lines.push(current)
    }
    current = context.measureText(word).width <= maxWidth ? word : pushLongWord(word)
  }
  if (current) lines.push(current)
  return lines.length ? lines : ['']
}

function receiptRightWidth(line: ReceiptLine, width: number) {
  const configuredWidth = Number(line.rightWidth || 130)
  return width === 384 ? Math.min(configuredWidth, 124) : configuredWidth
}

function receiptRasterWidth(paperWidthMm: number | string) {
  return Number(paperWidthMm) === 58 ? 384 : 576
}

function packReceiptRaster(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D
): ReceiptRaster {
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data
  const bytesPerRow = Math.ceil(canvas.width / 8)
  const packed = new Uint8Array(bytesPerRow * canvas.height)
  for (let row = 0; row < canvas.height; row += 1) {
    for (let column = 0; column < canvas.width; column += 1) {
      const offset = (row * canvas.width + column) * 4
      const alpha = (pixels[offset + 3] ?? 0) / 255
      const luminance =
        (pixels[offset] ?? 0) * 0.2126 +
        (pixels[offset + 1] ?? 0) * 0.7152 +
        (pixels[offset + 2] ?? 0) * 0.0722
      const compositedLuminance = 255 - (255 - luminance) * alpha
      if (compositedLuminance < 160) {
        const byte = row * bytesPerRow + Math.floor(column / 8)
        packed[byte] = (packed[byte] ?? 0) | (0x80 >> (column % 8))
      }
    }
  }
  let binary = ''
  const chunkSize = 0x8000
  for (let offset = 0; offset < packed.length; offset += chunkSize) {
    binary += String.fromCharCode(...packed.subarray(offset, offset + chunkSize))
  }
  return { widthPx: canvas.width, heightPx: canvas.height, dataBase64: btoa(binary) }
}

function expandReceiptLines(
  context: CanvasRenderingContext2D,
  lines: ReceiptLine[],
  width: number,
  padding: number
): ReceiptLine[] {
  return lines.flatMap((line) => {
    if (!line.wrap || line.separator || line.spacer) return line
    const size = Number(line.size || 18)
    context.font = `${line.bold ? '700' : '500'} ${size}px Arial, sans-serif`
    const indent = Number(line.indent || 0)
    const rightWidth = line.right ? receiptRightWidth(line, width) + 12 : 0
    const maxWidth = width - padding * 2 - indent - rightWidth
    return wrapReceiptText(context, line.text ?? '', maxWidth).map((text, index) => ({
      ...line,
      text,
      right: index === 0 ? line.right : undefined,
      wrap: false,
    }))
  })
}

function drawReceiptRaster(
  order: ReceiptRasterOrder,
  { paperWidthMm, reprint }: { paperWidthMm: number | string; reprint: boolean },
  logo: HTMLImageElement | null
) {
  const width = receiptRasterWidth(paperWidthMm)
  const padding = 24
  const lineGap = 12
  const { canvas, context } = receiptCanvas()
  const logoDimensions = receiptLogoDimensions(logo, width, padding)
  canvas.width = width
  const lines = expandReceiptLines(
    context,
    buildReceiptLines(order, { reprint, hasLogo: Boolean(logoDimensions) }),
    width,
    padding
  )
  const heights = lines.map((line) => {
    if (line.spacer) return Number(line.height || lineGap)
    if (line.separator) return 20
    return Number(line.size || 18) + Number(line.gap ?? lineGap)
  })
  canvas.height =
    padding * 2 +
    (logoDimensions ? logoDimensions.height + lineGap : 0) +
    heights.reduce((sum, height) => sum + height, 0)
  context.fillStyle = '#fff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.fillStyle = '#000'
  context.textBaseline = 'top'
  let y = padding
  if (logo && logoDimensions) {
    context.drawImage(
      logo,
      Math.round((width - logoDimensions.width) / 2),
      y,
      logoDimensions.width,
      logoDimensions.height
    )
    y += logoDimensions.height + lineGap
  }
  lines.forEach((line, index) => {
    const height = heights[index] ?? 0
    if (line.spacer) {
      y += height
      return
    }
    if (line.separator) {
      const dashWidth = 12
      const dashGap = 8
      for (let x = padding; x < width - padding; x += dashWidth + dashGap) {
        context.fillRect(x, y + 7, Math.min(dashWidth, width - padding - x), 2)
      }
      y += height
      return
    }
    const size = Number(line.size || 18)
    const text = line.text ?? ''
    context.font = `${line.bold ? '700' : '500'} ${size}px Arial, sans-serif`
    if (line.align === 'center') {
      context.textAlign = 'center'
      context.fillText(text, width / 2, y, width - padding * 2)
    } else {
      context.textAlign = 'left'
      const indent = Number(line.indent || 0)
      const rightWidth = line.right ? receiptRightWidth(line, width) : 0
      const availableWidth = width - padding * 2 - indent - (line.right ? rightWidth + 12 : 0)
      context.fillText(text, padding + indent, y, availableWidth)
      if (line.right) {
        context.textAlign = 'right'
        context.fillText(line.right, width - padding, y, rightWidth)
      }
    }
    y += height
  })

  return packReceiptRaster(canvas, context)
}

export async function renderReceiptRaster(
  order: ReceiptRasterOrder,
  { paperWidthMm = 80, reprint = false }: { paperWidthMm?: number | string; reprint?: boolean } = {}
): Promise<ReceiptRaster> {
  const logo = await loadReceiptLogo(order?.organization?.logo_url)
  return drawReceiptRaster(order, { paperWidthMm, reprint }, logo)
}

export function renderReceiptTestRaster(): ReceiptRaster {
  const { canvas, context } = receiptCanvas()

  canvas.width = testReceiptWidthPx
  canvas.height = testReceiptContentHeightPx + Math.round(receiptPrinterDpi * testReceiptFeedInches)
  context.fillStyle = '#fff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.fillStyle = '#000'
  context.font = '500 24px Arial, sans-serif'
  context.textBaseline = 'top'
  context.textAlign = 'left'
  context.fillText('test', 12, 12)

  return packReceiptRaster(canvas, context)
}
